import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import between from '@inquisitive/between';
import sortBy from 'lodash/sortBy';
import { useApolloClient } from '@apollo/client';
import { JSONContent } from '@tiptap/core';

import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import IconNotAllowed from 'approot/shared/icons/icon-not-allowed';
import { DropdownKebab } from 'approot/shared/dropdown/dropdown-kebab';
import {
  DropdownDivider,
  DropdownItem,
} from 'approot/shared/dropdown/dropdown-item.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ConfirmModal from 'approot/shared/confirm-modal/confirm-modal';
import { PStyled } from 'approot/shared/styles/typography.style';
import { getFirstSortValue, getSortByIndex } from './activity-builder.utils';
import { ExtraSectionIdentifiers } from './activity-builder.apollo';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import {
  useCreateNewSection,
  useDeleteSection,
  useUpdateSection,
  useUpdateSectionUsingParams,
} from './activity-builder-section.hooks';
import {
  useDuplicateActivity,
  useDuplicateActivityAsSubActivity,
  useDuplicateSubActivity,
  useDuplicateSubActivityAsActivity,
} from './activity-builder-duplicate.hooks';
import * as Styled from './activity-builder-slides.style';
import { useActivityBuilderContext } from './activity-builder.context';
import { getEditUrl } from './activity-builder-url.utils';
import { SectionOrSubSectionOverview } from './activity-builder.constants';
import { ACTIVITY_BUILDER_SLIDE_NAVIGATION } from './activity-builder-skip-links';
import customNanoId from 'lib/nanoid/nanoid';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import {
  getNextSectionToFocus,
  scrollSlideIntoView,
} from './activity-builder-slides.utils';
import {
  ActivityBuilderIntroPreview,
  ActivityBuilderPagePreview,
} from './page/activity-builder-page-preview';
import { writeSectionQueryById } from './activity-builder.apollo.utils';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  ACTIVITIES_PAGE_TYPE_TEACHER,
  DRAFT_ACTION_MOVE_SECTION,
} from 'global/constants';
import { InquisitiveActivitiesPageType } from 'global/graphql/scalars.type';
import {
  EDITOR_ACTIVITY_MENU_CONTAINER,
  EditorActivityControlButton,
  EditorActivityControls,
} from './editor/editor-activity-controls';
import { ActivitySettingsCheckpoint } from './components/activity/activity.style';
import IconPlus from 'approot/shared/icons/icon-plus';
import type { Pluck } from 'lib/gql/pluck';

const MAX_ACTIVITY_SLIDES = 30;
const MAX_SUB_ACTIVITY_SLIDES = 8;

const ActivityBuilderSlides = ({
  activeSectionId,
  sortedSectionsOverview,
  sectionsAndSubSectionsOverview,
  focusQuestion,
  phenomena,
  assessmentName,
  introBackground,
  authorId,
  setActiveSection,
}: {
  activeSectionId: string;
  setActiveSection: (id: string) => void;
  sortedSectionsOverview: Pluck<LessonActivitiesFragment, 'sectionsOverview'>[];
  sectionsAndSubSectionsOverview: SectionOrSubSectionOverview[];
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  assessmentName?: JSONContent | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  introBackground?: string | null;
  authorId?: number;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);

  const client = useApolloClient();
  const navigate = useNavigate();
  const { slug, userClassId, contentId, contentType } =
    useGetInteractiveLessonParams();

  const sectionToDelete = useRef<
    | {
        id: string;
        index: number;
        hasSubSlide: boolean;
        parentSectionId?: string;
      }
    | undefined
  >(undefined);
  const isInitialisedRef = useRef<boolean>(false);
  const [newId, setNewId] = useState<string[] | null>(null);

  const { clearActiveComponent } = useActivityBuilderContext();

  const { showError } = useSnackbar();
  const {
    isVisible: isDeleteVisible,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useSimpleModalState();

  const { createSection } = useCreateNewSection({
    onCompleted: result => {
      if (result.insertActivitiesSection) {
        const sortedSubSections = sortBy(
          result.insertActivitiesSection.subSections,
          'sort'
        );
        const subSectionId = sortedSubSections?.at(-1)?.id;

        // we do this so the main view has something to show
        writeSectionQueryById({
          client,
          data: result.insertActivitiesSection,
          contentId,
          contentType,
          sectionId: subSectionId || result.insertActivitiesSection.id,
        });
        setNewId(null);

        // focus either the sub-section or the section
        navigate(
          getEditUrl({
            slug,
            userClassId,
            sectionId: subSectionId || result.insertActivitiesSection.id,
            contentType,
          })
        );
        setActiveSection(subSectionId || result.insertActivitiesSection.id);
      }
    },
    onError: () => showError('There was an error creating a new section'),
    contentId,
    contentType,
  });

  const { updateSection } = useUpdateSection({
    onError: () => showError('There was an error updating a section'),
    contentId,
    contentType,
  });

  const { deleteSection } = useDeleteSection({
    contentId,
    contentType,
    onCompleted: () => {
      if (!sectionToDelete.current) return;

      const sectionIndex = sectionToDelete.current.index;

      const id = getNextSectionToFocus({
        currentSectionId: activeSectionId,
        deletedSectionId: sectionToDelete.current.id,
        parentSectionId: sectionToDelete.current.parentSectionId,
        sortedSections: sortedSectionsOverview,
        currentIndex: sectionIndex,
      });

      if (id) {
        navigate(getEditUrl({ slug, userClassId, sectionId: id, contentType }));
        setActiveSection(id);
      }
    },
    onError: () => showError('There was an error deleting the Activity'),
  });

  const { duplicateActivity } = useDuplicateActivity({
    onError: () => {
      showError('There was an error duplicating the Activity');
      setNewId(null);
    },
    onCompleted: result => {
      const subSections = result.duplicateActivitiesSection.subSections;
      if (subSections && subSections?.length > 0) {
        // write each sub-section to the cache
        subSections.forEach(s => {
          writeSectionQueryById({
            client,
            data: result.duplicateActivitiesSection,
            contentId,
            contentType,
            sectionId: s.id,
          });
        });
      }

      // write parent section to the cache
      writeSectionQueryById({
        client,
        data: result.duplicateActivitiesSection,
        contentId,
        contentType,
        sectionId: result.duplicateActivitiesSection.id,
      });
      scrollSlideIntoView({
        sectionId: result.duplicateActivitiesSection.id,
        containerRef,
      });
      setNewId(null);
    },
    contentId,
    contentType,
  });

  const { duplicateSubActivity } = useDuplicateSubActivity({
    onCompleted: result => {
      const subSection = result.duplicateActivitiesSubSection;
      if (subSection) {
        writeSectionQueryById({
          client,
          data: subSection,
          contentId,
          contentType,
          sectionId: subSection.id,
        });
      }
      scrollSlideIntoView({ sectionId: subSection.id, containerRef });
      setNewId(null);
    },
    onError: () => {
      showError('There was an error duplicating as a Sub-activity');
      setNewId(null);
    },
    contentId,
    contentType,
  });

  const { duplicateSubActivityAsActivity } = useDuplicateSubActivityAsActivity({
    onCompleted: result => {
      const section = result.duplicateActivitiesSubSectionAsSection;
      if (section) {
        writeSectionQueryById({
          client,
          data: section,
          contentId,
          contentType,
          sectionId: section.id,
        });
      }
      scrollSlideIntoView({ sectionId: section.id, containerRef });
      setNewId(null);
    },
    onError: () => {
      showError('There was an error duplicating the Sub-activity');
      setNewId(null);
    },
    contentId,
    contentType,
  });

  const { duplicateActivityAsSubActivity } = useDuplicateActivityAsSubActivity({
    onCompleted: result => {
      const section = result.duplicateActivitiesSectionAsSubSection;
      if (section) {
        writeSectionQueryById({
          client,
          data: section,
          contentId,
          contentType,
          sectionId: section.id,
        });
      }
      scrollSlideIntoView({ sectionId: section.id, containerRef });
      setNewId(null);
    },
    onError: () => {
      showError(
        'There was an error duplicating the Activity as a Sub-activity'
      );
      setNewId(null);
    },
    contentId,
    contentType,
  });

  const handleAddSubSection = useCallback(
    ({
      parentSectionId,
      subSections,
      index,
    }: {
      parentSectionId: string;
      subSections:
        | Pluck<
            LessonActivitiesFragment,
            'sectionsOverview.subSectionsOverview'
          >[]
        | null;
      index?: number;
    }) => {
      clearActiveComponent();

      const newSectionId = customNanoId();
      setNewId([newSectionId]);
      const sort = getSortByIndex({
        items: sortBy(subSections, 'sort') || [],
        index: index !== undefined ? index : (subSections || []).length - 1,
      });

      createSection({
        contentId,
        contentType,
        parentSectionId,
        sectionId: newSectionId,
        sort,
      });

      scrollSlideIntoView({ sectionId: newSectionId, containerRef });
    },
    [clearActiveComponent, createSection, contentId, contentType, containerRef]
  );

  const handleAddSection = useCallback(
    (props?: { parentSectionId?: string; index?: number; sort?: string }) => {
      const sort =
        props?.sort ||
        getSortByIndex({
          items: sortedSectionsOverview,
          index:
            props?.index !== undefined
              ? props.index
              : sortedSectionsOverview.length - 1,
        });
      const firstItemSort = between.between(between.lowest, between.highest);

      clearActiveComponent();

      const newSectionId = customNanoId();
      setNewId([newSectionId]);
      createSection({
        contentId,
        contentType,
        sort,
        pageType: ACTIVITIES_PAGE_TYPE_TEACHER,
        activityComponentData: {
          sort: firstItemSort,
          properties: {
            activitySettings: {
              differentiation: Differentiation.GreenCircle,
            },
          },
        },
        parentSectionId: props?.parentSectionId,
        sectionId: newSectionId,
      });

      scrollSlideIntoView({ sectionId: newSectionId, containerRef });
    },
    [
      contentId,
      sortedSectionsOverview,
      createSection,
      clearActiveComponent,
      contentType,
      containerRef,
    ]
  );

  const handleReorder = useCallback(
    (props: {
      sectionId: string;
      parentSectionId?: string;
      sort: string;
      pageType?: InquisitiveActivitiesPageType | null;
    }) => {
      updateSection({
        ...props,
        contentId,
        pageType: props.pageType,
        contentType,
        action: DRAFT_ACTION_MOVE_SECTION,
      });
    },
    [updateSection, contentId, contentType]
  );

  const handleDelete = useCallback(() => {
    if (!sectionToDelete.current) return;

    clearActiveComponent();

    deleteSection(
      {
        contentId,
        contentType,
        sectionId: sectionToDelete.current.id,
      },
      sectionToDelete.current.parentSectionId
    );
  }, [deleteSection, contentId, contentType, clearActiveComponent]);

  const handleSelect = useCallback(
    (sectionId: string) => {
      setActiveSection(sectionId);
      navigate(getEditUrl({ slug, userClassId, sectionId, contentType }));
      // make sure to deselect the active component
      clearActiveComponent();
    },
    [
      navigate,
      slug,
      userClassId,
      setActiveSection,
      clearActiveComponent,
      contentType,
    ]
  );

  const handleDuplicate = useCallback(
    (sectionId: string, sort: string) => {
      const subSectionOverviews = sortedSectionsOverview.find(
        s => s.id === sectionId
      )?.subSectionsOverview;

      const newIds: string[] = [];
      const newSectionId = customNanoId();
      newIds.push(newSectionId);

      duplicateActivity({
        contentId,
        contentType,
        sourceSectionId: sectionId,
        destinationSectionNewSort: sort,
        newDestinationSectionId: newSectionId,
        newDestinationSubSectionIds: subSectionOverviews
          ? subSectionOverviews.map(() => {
              const id = customNanoId();
              newIds.push(id);
              return id;
            })
          : null,
      });

      setNewId(() => newIds);
    },
    [contentId, contentType, sortedSectionsOverview, duplicateActivity]
  );

  const handleDuplicateSubActivity = useCallback(
    (
      subSection: Pluck<
        LessonActivitiesFragment,
        'sectionsOverview.subSectionsOverview'
      >,
      sort: string
    ) => {
      const newSectionId = customNanoId();
      setNewId([newSectionId]);
      duplicateSubActivity(
        {
          contentId,
          contentType,
          sourceSubSectionId: subSection.id,
          newDestinationSubSectionId: newSectionId,
          destinationSubSectionNewSort: sort,
        },
        subSection
      );
    },
    [contentId, contentType, duplicateSubActivity]
  );

  const handleDuplicateSubActivityAsActivity = useCallback(
    (
      subSection: Pluck<
        LessonActivitiesFragment,
        'sectionsOverview.subSectionsOverview'
      >,
      sort: string
    ) => {
      const newSectionId = customNanoId();
      setNewId([newSectionId]);
      duplicateSubActivityAsActivity(
        {
          contentId,
          contentType,
          sourceSubSectionId: subSection.id,
          newDestinationSectionId: newSectionId,
          destinationSectionNewSort: sort,
        },
        subSection
      );
    },
    [contentId, contentType, duplicateSubActivityAsActivity]
  );

  const handleDuplicateActivityAsSubActivity = useCallback(
    (sectionId: string, sort: string) => {
      const newSectionId = customNanoId();
      setNewId([newSectionId]);
      duplicateActivityAsSubActivity({
        contentId,
        contentType,
        sourceSectionId: sectionId,
        destinationSubSectionNewSort: sort,
        newDestinationSubSectionId: newSectionId,
      });
    },
    [contentId, contentType, duplicateActivityAsSubActivity]
  );

  useUpdateSectionUsingParams({
    sortedSections: sectionsAndSubSectionsOverview,
    isInitialisedRef,
  });

  const canAddActivity = sortedSectionsOverview.length < MAX_ACTIVITY_SLIDES;

  useEffect(() => {
    if (activeSectionId) {
      const el = document.getElementById(activeSectionId);
      if (el) {
        el.scrollIntoView();
      }
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div id={EDITOR_ACTIVITY_MENU_CONTAINER} />
      <Styled.ActivityBuilderSlidesNav
        id={ACTIVITY_BUILDER_SLIDE_NAVIGATION}
        ref={setContainerRef}
        tabIndex={-1}
        aria-label="Activity Previews"
        data-selector="lb-slides-nav"
      >
        {isDeleteVisible && sectionToDelete.current && (
          <ConfirmModal
            isVisible
            shortTitle={`Delete ${
              !!sectionToDelete.current.parentSectionId
                ? 'Sub-activity'
                : 'Activity'
            }`}
            confirmButtonText={`Delete ${
              !!sectionToDelete.current.parentSectionId
                ? 'Sub-activity'
                : `Activity ${sectionToDelete.current.index + 1}`
            }`}
            handleClose={closeDeleteModal}
            handleConfirm={handleDelete}
          >
            <PStyled marginBottom={0}>
              {!!sectionToDelete.current.parentSectionId ? (
                <>Are you sure you want to delete this Sub-activity?</>
              ) : (
                <>
                  Are you sure you want to delete Activity{' '}
                  {sectionToDelete.current.index + 1}
                  {sectionToDelete.current.hasSubSlide
                    ? ' and it’s Sub-activity'
                    : ''}
                  ?
                </>
              )}
            </PStyled>
          </ConfirmModal>
        )}
        <Styled.ActivityBuilderSlideItems>
          <Styled.ActivityBuilderSlideItem
            id={ExtraSectionIdentifiers.Introduction}
            $isActive={ExtraSectionIdentifiers.Introduction === activeSectionId}
            data-selector="introduction"
          >
            <Styled.ActivityBuilderSlide
              type="button"
              onClick={() => handleSelect(ExtraSectionIdentifiers.Introduction)}
              aria-label="Click to view activity"
            >
              <Styled.ActivityBuilderSlideIndicator>
                0
              </Styled.ActivityBuilderSlideIndicator>
              <Styled.ActivityBuilderSlideInner>
                <ActivityBuilderIntroPreview
                  focusQuestion={focusQuestion}
                  phenomena={phenomena}
                  assessmentName={assessmentName}
                  introBackground={introBackground}
                  authorId={authorId}
                />
              </Styled.ActivityBuilderSlideInner>
            </Styled.ActivityBuilderSlide>
          </Styled.ActivityBuilderSlideItem>

          {/* below the intro we can only add a new Activity */}
          <EditorActivityControlButton
            disabled={!canAddActivity}
            onClick={() =>
              handleAddSection({
                sort: getFirstSortValue(sortedSectionsOverview),
              })
            }
          />

          {sortedSectionsOverview.map((section, sectionIndex) => {
            const canAddSubActivity =
              section.subSectionsOverview === null ||
              section.subSectionsOverview.length < MAX_SUB_ACTIVITY_SLIDES;
            const sortedSubSections = sortBy(
              section.subSectionsOverview,
              'sort'
            );

            const differentiation =
              section.differentiation || Differentiation.GreenCircle;

            return (
              <Fragment key={section.id}>
                <Styled.ActivityBuilderSlideItem
                  id={section.id}
                  data-selector={`lb-slide-${sectionIndex + 1}`}
                  $isActive={section.id === activeSectionId}
                  $differentiation={differentiation}
                >
                  <Styled.ActivityBuilderSlide
                    type="button"
                    onClick={() =>
                      handleSelect(sortedSectionsOverview[sectionIndex].id)
                    }
                    aria-label="Click to view activity"
                  >
                    <Styled.ActivityBuilderSlideIndicator>
                      {sectionIndex + 1}
                    </Styled.ActivityBuilderSlideIndicator>
                    {section.checkPoint && (
                      <Styled.ActivityBuilderSlidePinWrapper>
                        <ActivitySettingsCheckpoint
                          $isActive
                          id="icon_star"
                          width={24}
                          height={24}
                        />
                      </Styled.ActivityBuilderSlidePinWrapper>
                    )}
                    <Styled.ActivityBuilderSlideInner>
                      <ActivityBuilderPagePreview
                        id={sortedSectionsOverview[sectionIndex].id}
                        contentId={contentId}
                        contentType={contentType}
                        classId={userClassId}
                        canFetch={!newId?.includes(section.id)}
                        initialVisibility={
                          activeSectionId ===
                          sortedSectionsOverview[sectionIndex].id
                        }
                        parentElement={containerRef}
                      />
                    </Styled.ActivityBuilderSlideInner>
                  </Styled.ActivityBuilderSlide>

                  <Styled.ActivityBuilderSlideKebab>
                    <DropdownKebab
                      menuWidth={230}
                      aria-label={`Options for activity ${sectionIndex + 1}`}
                      $isSmallToggle
                      borderRadius={4}
                      placement="right-start"
                      menuContainerElement={EDITOR_ACTIVITY_MENU_CONTAINER}
                      dropdownContents={
                        <>
                          {canAddActivity && (
                            <DropdownItem
                              key="add-activity"
                              onClick={() =>
                                handleAddSection({ index: sectionIndex })
                              }
                              data-selector="option-add-activity"
                            >
                              Add Activity
                            </DropdownItem>
                          )}
                          {canAddSubActivity && (
                            <DropdownItem
                              key="add-sub-activity"
                              onClick={() =>
                                handleAddSubSection({
                                  parentSectionId: section.id,
                                  subSections: sortedSubSections,
                                })
                              }
                              data-selector="option-add-sub-activity"
                            >
                              Add Sub-activity
                            </DropdownItem>
                          )}
                          {sectionIndex > 0 && (
                            <DropdownItem
                              key="up"
                              onClick={() =>
                                handleReorder({
                                  sectionId: section.id,
                                  pageType: section.pageType,
                                  sort: getSortByIndex({
                                    items: sortedSectionsOverview,
                                    index: sectionIndex - 2,
                                  }),
                                })
                              }
                              data-selector="option-move-up"
                            >
                              Move Up
                            </DropdownItem>
                          )}
                          {sectionIndex + 1 < sortedSectionsOverview.length && (
                            <DropdownItem
                              key="down"
                              onClick={() =>
                                handleReorder({
                                  sectionId: section.id,
                                  pageType: section.pageType,
                                  sort: getSortByIndex({
                                    items: sortedSectionsOverview,
                                    index: sectionIndex + 1,
                                  }),
                                })
                              }
                              data-selector="option-move-down"
                            >
                              Move Down
                            </DropdownItem>
                          )}
                          {canAddActivity && (
                            <DropdownItem
                              key="duplicate"
                              onClick={() =>
                                handleDuplicate(
                                  section.id,
                                  getSortByIndex({
                                    items: sortedSectionsOverview,
                                    index: sectionIndex,
                                  })
                                )
                              }
                              data-selector="option-duplicate-as-activity"
                            >
                              Duplicate as Activity
                            </DropdownItem>
                          )}
                          {canAddSubActivity && (
                            <DropdownItem
                              key="duplicate-as-subactivity"
                              onClick={() =>
                                handleDuplicateActivityAsSubActivity(
                                  section.id,
                                  getSortByIndex({
                                    items: sortedSubSections,
                                    index: sortedSubSections.length - 1,
                                  })
                                )
                              }
                              data-selector="option-duplicate-as-sub-activity"
                            >
                              Duplicate as Sub-activity
                            </DropdownItem>
                          )}
                          <DropdownDivider />
                          <DropdownItem
                            key="delete"
                            $isNegative
                            onClick={() => {
                              sectionToDelete.current = {
                                index: sectionIndex,
                                id: section.id,
                                hasSubSlide: !!(
                                  section?.subSectionsOverview?.length &&
                                  section?.subSectionsOverview.length > 0
                                ),
                              };
                              openDeleteModal();
                            }}
                            data-selector="option-delete"
                          >
                            Delete
                          </DropdownItem>
                        </>
                      }
                    />
                  </Styled.ActivityBuilderSlideKebab>
                </Styled.ActivityBuilderSlideItem>

                <li
                  data-selector={`add-options-after-slide-${sectionIndex + 1}`}
                >
                  {canAddActivity || canAddSubActivity ? (
                    <EditorActivityControls
                      canAddActivity={canAddActivity}
                      canAddSubActivity={canAddSubActivity}
                      onAddActivity={() =>
                        handleAddSection({ index: sectionIndex })
                      }
                      onAddSubActivity={() =>
                        handleAddSubSection({
                          parentSectionId: section.id,
                          subSections: sortedSubSections,
                        })
                      }
                    />
                  ) : (
                    <EditorActivityControlButton disabled />
                  )}
                </li>

                {sortedSubSections.length > 0 && (
                  <Styled.ActivityBuilderSubSlideUl>
                    {sortedSubSections.map((subSection, subSectionIndex) => {
                      return (
                        <Fragment key={subSection.id}>
                          <Styled.ActivityBuilderSlideItemSubSlide
                            id={subSection.id}
                            key={subSection.id}
                            $isActive={subSection.id === activeSectionId}
                            $differentiation={differentiation}
                            data-selector={`lb-sub-slide-${
                              sectionIndex + 1
                            }-${subSectionIndex + 1}`}
                          >
                            <Styled.ActivityBuilderSubSlide
                              type="button"
                              aria-label="Click to view Activity"
                              onClick={() => handleSelect(subSection.id)}
                            >
                              <Styled.ActivityBuilderSlideInner>
                                <ActivityBuilderPagePreview
                                  id={subSection.id}
                                  contentId={contentId}
                                  contentType={contentType}
                                  classId={userClassId}
                                  canFetch={!newId?.includes(subSection.id)}
                                  initialVisibility={
                                    activeSectionId === subSection.id
                                  }
                                  parentElement={containerRef}
                                />
                              </Styled.ActivityBuilderSlideInner>
                            </Styled.ActivityBuilderSubSlide>

                            <Styled.ActivityBuilderSlideKebab>
                              <DropdownKebab
                                menuWidth={228}
                                placement="right-start"
                                menuContainerElement={
                                  EDITOR_ACTIVITY_MENU_CONTAINER
                                }
                                aria-label={`Options for activity ${
                                  sectionIndex + 1
                                }'s sub-activity`}
                                $isSmallToggle
                                borderRadius={4}
                                dropdownContents={
                                  <>
                                    {canAddActivity && (
                                      <DropdownItem
                                        key="add-activity"
                                        onClick={() =>
                                          handleAddSection({
                                            index: sectionIndex,
                                          })
                                        }
                                        data-selector="option-add-activity"
                                      >
                                        Add Activity
                                      </DropdownItem>
                                    )}
                                    {canAddSubActivity && (
                                      <DropdownItem
                                        key="add-sub-activity"
                                        onClick={() =>
                                          handleAddSubSection({
                                            parentSectionId: section.id,
                                            subSections: sortedSubSections,
                                            index: subSectionIndex,
                                          })
                                        }
                                        data-selector="option-add-sub-activity"
                                      >
                                        Add Sub-activity
                                      </DropdownItem>
                                    )}
                                    {subSectionIndex > 0 && (
                                      <DropdownItem
                                        key="up"
                                        onClick={() =>
                                          handleReorder({
                                            sectionId: subSection.id,
                                            parentSectionId: section.id,
                                            sort: getSortByIndex({
                                              items: sortedSubSections,
                                              index: subSectionIndex - 2,
                                            }),
                                          })
                                        }
                                        data-selector="option-move-up"
                                      >
                                        Move Up
                                      </DropdownItem>
                                    )}
                                    {subSectionIndex + 1 <
                                      sortedSubSections.length && (
                                      <DropdownItem
                                        key="down"
                                        onClick={() =>
                                          handleReorder({
                                            sectionId: subSection.id,
                                            parentSectionId: section.id,
                                            sort: getSortByIndex({
                                              items: sortedSubSections,
                                              index: subSectionIndex + 1,
                                            }),
                                          })
                                        }
                                        data-selector="option-move-down"
                                      >
                                        Move Down
                                      </DropdownItem>
                                    )}
                                    {canAddActivity && (
                                      <DropdownItem
                                        key="duplicate-sub-activity-as-activity"
                                        onClick={() =>
                                          handleDuplicateSubActivityAsActivity(
                                            subSection,
                                            getSortByIndex({
                                              items: sortedSectionsOverview,
                                              index: sectionIndex,
                                            })
                                          )
                                        }
                                        data-selector="option-duplicate-as-activity"
                                      >
                                        Duplicate as Activity
                                      </DropdownItem>
                                    )}
                                    {canAddSubActivity && (
                                      <DropdownItem
                                        key="duplicate-sub-activity"
                                        onClick={() =>
                                          handleDuplicateSubActivity(
                                            subSection,
                                            getSortByIndex({
                                              items: sortedSubSections,
                                              index: subSectionIndex,
                                            })
                                          )
                                        }
                                        data-selector="option-duplicate-as-sub-activity"
                                      >
                                        Duplicate as Sub-activity
                                      </DropdownItem>
                                    )}
                                    {(sortedSubSections.length > 1 ||
                                      canAddSubActivity) && <DropdownDivider />}
                                    <DropdownItem
                                      key="delete"
                                      $isNegative
                                      onClick={() => {
                                        sectionToDelete.current = {
                                          index: subSectionIndex,
                                          id: subSection.id,
                                          hasSubSlide: false,
                                          parentSectionId:
                                            section.id || undefined,
                                        };
                                        openDeleteModal();
                                      }}
                                      data-selector="option-delete"
                                    >
                                      Delete
                                    </DropdownItem>
                                  </>
                                }
                              />
                            </Styled.ActivityBuilderSlideKebab>
                          </Styled.ActivityBuilderSlideItemSubSlide>
                          <li
                            data-selector={`add-options-after-slide-${
                              sectionIndex + 1
                            }-subslide-${subSectionIndex + 1}`}
                          >
                            {(canAddSubActivity || canAddActivity) &&
                            subSectionIndex === sortedSubSections.length - 1 ? (
                              <EditorActivityControls
                                canAddActivity={canAddActivity}
                                canAddSubActivity={canAddSubActivity}
                                onAddActivity={() =>
                                  handleAddSection({ index: sectionIndex })
                                }
                                onAddSubActivity={() =>
                                  handleAddSubSection({
                                    parentSectionId: section.id,
                                    subSections: sortedSubSections,
                                    index: subSectionIndex,
                                  })
                                }
                              />
                            ) : (
                              <EditorActivityControlButton
                                disabled={!canAddSubActivity}
                                onClick={() =>
                                  handleAddSubSection({
                                    parentSectionId: section.id,
                                    subSections: sortedSubSections,
                                    index: subSectionIndex,
                                  })
                                }
                              />
                            )}
                          </li>
                        </Fragment>
                      );
                    })}
                  </Styled.ActivityBuilderSubSlideUl>
                )}
              </Fragment>
            );
          })}
          <Styled.ActivityBuilderAddSlideItem>
            {!canAddActivity ? (
              <TooltipWrapper hoverText="Maximum number of activities reached">
                <Styled.ActivityBuilderSlideDisabled aria-label="Add New Activity is disabled as maximum number of activities reached">
                  <IconNotAllowed width={32} height={32} />
                </Styled.ActivityBuilderSlideDisabled>
              </TooltipWrapper>
            ) : (
              <Styled.ActivityBuilderSlideAdd
                type="button"
                data-selector="lb-add-new-slide-btn"
                aria-label="Add New Activity"
                onClick={() => handleAddSection()}
              >
                <IconPlus width={32} height={32} />
              </Styled.ActivityBuilderSlideAdd>
            )}
          </Styled.ActivityBuilderAddSlideItem>
        </Styled.ActivityBuilderSlideItems>
      </Styled.ActivityBuilderSlidesNav>
    </>
  );
};

export default ActivityBuilderSlides;
